import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { ApiError, toApiError } from 'shared/errors';
import { RootState } from 'store/store';
import { GameDetails, GameDetailsScore } from '../domain/interfaces/game-details';
import { GameEventDto } from '../domain/interfaces/game-event-dto';
import { GamePeriodsSettings, getPeriodsSettings } from '../domain/utils/match-periods-settings';
import gameRepository from '../domain/repositories/game-repository';

const PREFIX = 'games';

export const fetchGameDetails = createAsyncThunk<GameDetails | null, { gameId: number; championshipId: number }>(
  `${PREFIX}/fetchDetails`,
  async (payload, { getState }) => {
    try {
      const state = getState() as RootState;
      const settings = state.championships.championship.data?.byId[payload.championshipId]?.settings || ({} as ChampionshipSettings);
      const periodsSettings = getPeriodsSettings(settings);
      return await gameRepository.fetchGameDetails(payload.gameId, periodsSettings);
    } catch (err) {
      console.error(`${PREFIX}/fetchDetails error:`, err);
      throw err;
    }
  },
);

export type ModifyGameEventData = {
  data: GameEventDto;
  periodSettings: GamePeriodsSettings;
};

export const addGameEvent = createAction<ModifyGameEventData>(`${PREFIX}/addGameEvent`);
export const editGameEvent = createAction<ModifyGameEventData>(`${PREFIX}/editGameEvent`);
export const deleteGameEvent = createAction<number>(`${PREFIX}/deleteGameEvent`);
export const editGameScore = createAction<GameDetailsScore>(`${PREFIX}/editGameScore`);

export const changeGameDetailsVideoUrl = createAction<string>(`${PREFIX}/changeGameDetailsVideoUrl`);

/** Сохранённые детали матча плюс причина, по которой не удалось отметить участников.
 *
 * Результат (`upsertMatch`) сохраняется первым и от участников не зависит: бэкенд не проверяет
 * участие игроков в событиях. Отметка участников (`bulkChangeMatchParticipationStatus`) идёт
 * второй и может отказать по игроку — 4010, если его нет в заявках, а его команды нет в матче
 * (`MatchParticipationMutation.php`, `checkIfUserCanParticipate`). Такой отказ не должен мешать
 * записать счёт, поэтому он едет данными, а предупреждение показывает экран (ревью PR #574). */
export interface SaveGameDetailsResult {
  details: GameDetails;
  participantsError?: ApiError;
}

export const saveGameDetails = createAsyncThunk<SaveGameDetailsResult, GameDetails>(
  `${PREFIX}/saveGameDetails`,
  async (payload, { getState }) => {
    try {
      const state = getState() as RootState;
      const settings = state.championships.championship.data?.byId[payload.championship.id]?.settings || ({} as ChampionshipSettings);
      const periodsSettings = getPeriodsSettings(settings);

      const eventsPlayerIds = payload.events
        .reduce<number[]>((results, event) => [...results, ...event.playerIds], [])
        .filter(Boolean);

      const oldParticipantIds = payload.participants;
      const newParticipantIds = Array.from(new Set([...oldParticipantIds, ...eventsPlayerIds]));
      const participantsChanged = newParticipantIds.some((id) => !oldParticipantIds.includes(id));

      const details = await gameRepository.updateGameDetails(payload, periodsSettings);
      if (!participantsChanged) return { details };

      try {
        await gameRepository.updateMatchParticipants(payload.id, newParticipantIds);
        // Ответ `upsertMatch` пришёл до отметки — новых участников в нём ещё нет.
        return { details: { ...details, participants: newParticipantIds } };
      } catch (error) {
        // Бэкенд отмечает игроков по одному: при отказе на N-м предыдущие уже отмечены. Следующее
        // сохранение повторит отметку — `upsert` на бэкенде идемпотентен.
        return { details, participantsError: toApiError(error) };
      }
    } catch (err) {
      console.error(`${PREFIX}/saveGameDetails error:`, err);
      throw err;
    }
  },
);
